import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useCurrentEditor } from '@tiptap/react';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import MenuList from '@components/MenuList';
import { couponsActions } from '@actions';
import { useTranslation, useCouponsTemplates } from '@hooks';
import { addTipTapMarkTag } from '@helpers/text-editor';
import { TicketIcon } from 'assets/images/icons';

const CouponsMenu = ({ disabled, setMessage, context }) => {
    const { t } = useTranslation();
    const { editor } = useCurrentEditor();
    const isMobile = useMediaQuery('@media (max-width: 600px)');
    const dispatch = useDispatch();

    const [anchorElMain, setAnchorElMain] = useState(null);
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

    const companies = useSelector(state => state.account?.account?.companies || []);
    const couponsAll = useSelector(state => state.coupons.dataAll);
    const { getCouponsOptionsByLocationsId } = useCouponsTemplates();

    useEffect(() => {
        if (couponsAll.length === 0) {
            dispatch(couponsActions.getAll());
        }
    }, [couponsAll.length]);

    const handleOpenMain = event => {
        if (isMobile) {
            setMobileMenuOpen(true);
        } else {
            setAnchorElMain(event.currentTarget);
        }
    };

    const handleCloseMain = () => setAnchorElMain(null);

    const onClickCoupon = coupon => {
        if (!editor) {
            return;
        }
        const endPosition = editor.state.doc.content.size;
        const locationName = companies.find(c => c.companyId === context?.locationId)?.locationName;

        let newMessage = '';
        if (coupon.defaultMessage) {
            newMessage = coupon.defaultMessage
                ?.replace(/\[name\]/g, context?.firstName || '')
                ?.replace(/\[last_name\]/g, context?.lastName || '')
                ?.replace(/\[location\]/g, locationName);
        }
        const placeholderWithId = `[coupon:${coupon.id}]`;
        const messageForEditor = `${newMessage ? `${newMessage} ` : ''}${placeholderWithId}`;
        // INFO: editor has paragraph tags, so we need to subtract 2
        if (endPosition - 2 === 0 || coupon.defaultMessage?.length > 0) {
            editor.commands.setContent(addTipTapMarkTag(messageForEditor));
        } else {
            editor
                .chain()
                .focus('end')
                .insertContent(newMessage ? ` ${newMessage} ` : ' ')
                .insertContent(addTipTapMarkTag(placeholderWithId))
                .run();
        }
        setMessage(JSON.stringify(editor.getJSON()));
        setMobileMenuOpen(false);
        setAnchorElMain(null);
    };

    const mapCouponPropsToOption = coupon => ({
        value: coupon.id,
        icon: null,
        text: coupon.title,
        onClick: () => onClickCoupon(coupon),
    });

    const mapMenu = useMemo(
        () => getCouponsOptionsByLocationsId(context?.locationId, mapCouponPropsToOption),
        [context?.locationId, getCouponsOptionsByLocationsId],
    );

    return (
        <>
            <MenuList
                options={mapMenu}
                anchorEl={anchorElMain}
                onCloseMenu={handleCloseMain}
                openModal={isMobileMenuOpen}
                handleCloseModal={() => setMobileMenuOpen(false)}
                mainTitle={t('Sidebar.coupons')}
                menuButton={
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={handleOpenMain}
                        disabled={disabled}
                        sx={{
                            height: 18,
                            width: 18,
                            padding: '2px !important',
                            '& svg path': { fill: 'black', fillOpacity: 0.6 },
                        }}
                    >
                        <TicketIcon />
                    </IconButton>
                }
            />
        </>
    );
};

export default CouponsMenu;
